import React from 'react';
import { Link } from 'react-router';
import SEO from '../components/SEO';
import GlowCard from '../components/GlowCard';
import AIImage from '../components/AIImage';
import { trackEvent } from '../components/analytics';

  const SmsRegulatory: React.FC = () => {
    return (
      <div className="animate-fade-in-up">
        <SEO
          title="SMS Software for Civil Aviation Authorities and ANSPs"
          description="ICAO Annex 19 and Doc 9859 aligned SMS software for CAAs and ANSPs supporting audit-grade traceability, oversight workflows, and authority system-of-record governance."
        />

        <section className="py-24 bg-slate-950 border-b border-white/5">
          <div className="max-w-7xl mx-auto px-4 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h1 className="text-5xl lg:text-7xl font-bold font-heading mb-8 leading-tight">
                SMS software for
                 <span className="text-transparent bg-clip-text bg-gradient-to-r from-accent via-white to-brandDeep"> aviation authorities and ANSPs</span>
              </h1>
              <p className="text-xl text-slate-400 mb-6 leading-relaxed">
                ICAO Annex 19 and Doc 9859 aligned SMS for CAAs and ANSPs with audit-grade traceability, oversight workflows, and defensible authority records.
              </p>
              <p className="text-xs uppercase tracking-widest text-slate-500 mb-10">
                For Civil Aviation Authorities and Air Navigation Service Providers.
              </p>
              <Link
                to="/contact"
                className="inline-flex px-10 py-4 bg-accent text-primary font-bold rounded-xl"
                onClick={() => trackEvent('demo_request', { form_id: 'sms_regulatory_hero_briefing', module: 'sms' })}
              >
                Request an SMS Regulatory Briefing
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 relative p-1 bg-slate-800 rounded-2xl overflow-hidden border border-white/10">
                <AIImage
                  prompt="A formal, institutional aviation oversight composition with stacked governance documents, seals, and monochrome blueprint lines, no UI, no charts, authoritative tone"
                  alt="Regulatory safety governance overview"
                  className="w-full opacity-95"
                  aspectRatio="16:9"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-24 px-4 border-b border-white/5">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl lg:text-5xl font-bold font-heading mb-8 text-white">Why Authority-Level SMS Fails in
              Practice</h2>
            <GlowCard>
            <ul className="text-slate-400 text-sm space-y-3 mb-6">
              <li><h3 className="text-xl font-bold text-white mb-3">SMS evidence spread across documents, tools, and inboxes</h3></li>
              <li><h3 className="text-xl font-bold text-white mb-3">Manual hazard tracking and MoC approvals</h3></li>
              <li><h3 className="text-xl font-bold text-white mb-3">SPIs calculated offline and reported late</h3></li>
              <li><h3 className="text-xl font-bold text-white mb-3">Audit preparation replaces continuous oversight</h3></li>
              <li><h3 className="text-xl font-bold text-white mb-3">Oversight decisions that cannot be reconstructed months later</h3></li>
            </ul>
            <p className="text-slate-400 text-base leading-relaxed">
SMS fails when it is treated as a reporting obligation instead of a permanent
governance and oversight system.              </p>
              </GlowCard>
          </div>
        </section>

        <section className="py-24 px-4 bg-slate-950/50 border-b border-white/5">
          <div className="max-w-7xl mx-auto">
            <div className="p-1 bg-slate-800 rounded-2xl overflow-hidden border border-white/10">
              <AIImage
                prompt="Institutional monochrome diagram on a dark background with centered vertical text lines exactly as follows: 'Airlines / Operators / ANSPs', then a down arrow, 'Safety Evidence', then a down arrow, 'Authority SMS', on the next line 'System of Record', followed by bullet lines '• Immutable logs', '• Audit trails', '• Oversight views', then a down arrow, 'Regulatory Oversight'. Minimal document-like style, no icons, no UI."
                alt="Authority SMS system of record"
                className="w-full opacity-95"
                aspectRatio="16:9"
              />
            </div>
            <p className="text-slate-500 text-sm mt-4 text-center">
              Authority-owned safety system of record with traceable evidence.
            </p>
          </div>
        </section>

        <section className="py-24 px-4 bg-slate-950/50 border-b border-white/5">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl lg:text-5xl font-bold font-heading mb-8 text-white">SMS as a System of Record</h2>
            <GlowCard>
            <ul className="text-slate-400 text-sm space-y-3 mb-6">
              <li><h3 className="text-xl font-bold text-white mb-3">Single authority-owned SMS repository</h3></li>
              <li><h3 className="text-xl font-bold text-white mb-3">Immutable audit trails</h3></li>
              <li><h3 className="text-xl font-bold text-white mb-3">Role-based approvals</h3></li>
              <li><h3 className="text-xl font-bold text-white mb-3">Evidence always traceable to source</h3></li>
            </ul>
            <p className="text-slate-400 text-base leading-relaxed">
              AviaGov does not automate safety decisions. It enforces traceability, accountability, and compliance. It ensures that every oversight decision can be justified, reviewed, and
defended over time.
              </p>
              </GlowCard>
          </div>
        </section>

        <section className="py-24 px-4 border-b border-white/5">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl lg:text-5xl font-bold font-heading mb-8 text-white">Built for ICAO Annex 19
  Compliance</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <GlowCard>
                <h3 className="text-xl font-bold text-white mb-3">Safety Policy & Objectives</h3>
                <p className="text-slate-400 text-sm">Authority policy, roles, non-punitive reporting governance</p>
              </GlowCard>
              <GlowCard>
                <h3 className="text-xl font-bold text-white mb-3">Safety Risk Management (SRM)</h3>
                <p className="text-slate-400 text-sm">Hazard registry, risk assessment, mitigation lifecycle</p>
              </GlowCard>
              <GlowCard>
                <h3 className="text-xl font-bold text-white mb-3">Safety Assurance (SA)</h3>
                <p className="text-slate-400 text-sm">SPIs, monitoring, corrective action tracking</p>
              </GlowCard>
              <GlowCard>
                <h3 className="text-xl font-bold text-white mb-3">Safety Promotion</h3>
                <p className="text-slate-400 text-sm">Training linkage (LMS), competency evidence, communication</p>
              </GlowCard>
            </div>
          </div>
        </section>

        <section className="py-24 px-4 bg-slate-950/50 border-b border-white/5">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl lg:text-5xl font-bold font-heading mb-8 text-white">What Makes It Regulatory-Grade</h2>
            <GlowCard>
            <ul className="text-slate-400 text-sm space-y-3">
              <li><h3 className="text-xl font-bold text-white mb-3">Oversight-centric design</h3></li>
              <li><h3 className="text-xl font-bold text-white mb-3">Multi-organization capability</h3></li>
              <li><h3 className="text-xl font-bold text-white mb-3">Read-only external audit views</h3></li>
              <li><h3 className="text-xl font-bold text-white mb-3">Immutable logs</h3></li>
              <li><h3 className="text-xl font-bold text-white mb-3">No deletion of safety history</h3></li>
              </ul>
              </GlowCard>
          </div>
        </section>

        <section className="py-24 px-4">
          <div className="max-w-6xl mx-auto text-center">
            <Link
              to="/contact"
              className="inline-flex px-10 py-4 bg-accent text-primary font-bold rounded-xl"
              onClick={() => trackEvent('demo_request', { form_id: 'sms_regulatory_cta_briefing', module: 'sms' })}
            >
              Request an SMS Regulatory Briefing
            </Link>
            <p className="text-slate-400 text-sm mt-4">
              15–30 minutes. No sales pitch. Authority-level discussion.
            </p>
          </div>
        </section>
      </div>
    );
  };

  export default SmsRegulatory;
